import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/design-system/components/ui";
import { PortalDetailShell } from "../components/PortalDetailShell";
import { PortalErrorBoundary } from "../components/PortalErrorBoundary";
import { StatusPill } from "../components/StatusPill";
import { StepIndicator } from "../components/StepIndicator";
import { PortalMarkdown } from "../components/PortalMarkdown";
import { ResponseStep, type PortalResponse } from "../components/ResponseStep";
import { usePortalAuth } from "../PortalAuthContext";
import { portalPackagesApi } from "../api/packages";
import { isEditableStatus } from "../lib/statusMap";
import { staffNoteFor } from "../lib/itemStatus";
import { portalLoginPathFor } from "../lib/returnTo";
import type { PortalItemDto, PortalPackageDto } from "../api/types";
import { FormItemSurface } from "./FormItemSurface";
import { UploadItemSurface } from "./UploadItemSurface";
import { ESignItemSurface } from "./ESignItemSurface";
import { EducationItemSurface } from "./EducationItemSurface";

// External Intake & Collection v1.0 PR 4 §2.3 — per-item dispatcher.
// Fetches the package on mount, finds the requested item, and renders
// the surface that matches its Kind. Submission flows update local state
// in-place so the recipient sees their new status without a refresh.
// A visitor with no portal session is sent to sign-in with a returnTo back
// here (A17), and every surface renders through ResponseStep so a returned
// item asks for the recipient's response first.

export function ItemPage() {
  const { packageId } = useParams<{ packageId: string }>();
  return (
    <PortalErrorBoundary
      fallback={(retry) => (
        <PortalDetailShell pageTitle="Item">
          <div
            className="mt-space-4 flex flex-col items-start gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure"
            role="alert"
            data-testid="item-page-error"
          >
            <h2 className="text-h4 font-display text-onyx m-0">Something went wrong</h2>
            <p className="text-small text-onyx m-0">
              This item hit an unexpected error. Your answers are saved as you go — try again,
              or head back to your checklist.
            </p>
            <div className="flex flex-wrap items-center gap-space-3">
              <Button variant="secondary" onClick={retry}>
                Try again
              </Button>
              <Link
                to={`/portal/packages/${packageId}`}
                className="text-small underline text-onyx"
              >
                Back to checklist
              </Link>
            </div>
          </div>
        </PortalDetailShell>
      )}
    >
      <ItemPageInner />
    </PortalErrorBoundary>
  );
}

function ItemPageInner() {
  const { packageId, itemId } = useParams<{ packageId: string; itemId: string }>();
  const auth = usePortalAuth();
  const location = useLocation();
  const [pkg, setPkg] = useState<PortalPackageDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  // A19 — true while an in-house signature is being recorded; "Back to
  // checklist" is locked so the recipient can't walk away mid-seal.
  const [signingBusy, setSigningBusy] = useState(false);

  useEffect(() => {
    if (!packageId) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await portalPackagesApi.get(packageId);
        if (!cancelled) setPkg(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load this item.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [packageId]);

  const item = pkg?.items.find((i) => i.id === itemId) ?? null;

  function updateItem(next: PortalItemDto) {
    setPkg((prev) =>
      prev ? { ...prev, items: prev.items.map((i) => (i.id === next.id ? next : i)) } : prev,
    );
  }

  // Approval chains — after the caller endorses or signs, re-read the package
  // so the chain view (every stage's status, whose turn it is) is the
  // server's, not a guess. A failed re-read keeps what is on screen.
  const reload = useCallback(async () => {
    if (!packageId) return;
    try {
      setPkg(await portalPackagesApi.get(packageId));
    } catch {
      // Non-fatal: the surface already shows the caller's own step as done.
    }
  }, [packageId]);

  // Download the filed document (signed/generated PDF or uploaded file) the
  // recipient owns. The SAS opens in a new tab, matching the materials /
  // education download pattern; scoping is enforced server-side.
  async function downloadDocument(id: string) {
    setDocError(null);
    try {
      const dl = await portalPackagesApi.getItemDocument(id);
      window.open(dl.sasUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setDocError(err instanceof Error ? err.message : "Couldn't open that document.");
    }
  }

  if (!auth.loading && !auth.me) {
    return <Navigate to={portalLoginPathFor(location.pathname + location.search)} replace />;
  }

  const staffNote = item ? staffNoteFor(item) : null;
  const editable = item ? isEditableStatus(item.status) : false;
  // Approval chains — a chain item is driven ONLY by its chain view: no step
  // indicator, no review-loop Respond step, no staff note, and its download
  // lives on the chain surface (once every party has signed).
  const chain = item?.approvalChain ?? null;

  const surfaces = (response: PortalResponse | null) =>
    item && (
      <>
        {item.kind === "Form" && (
          <FormItemSurface
            item={item}
            onUpdated={updateItem}
            disabled={!editable}
            response={response}
            onSigningBusyChange={setSigningBusy}
            onReload={reload}
          />
        )}
        {item.kind === "Upload" && (
          <UploadItemSurface
            item={item}
            onUpdated={updateItem}
            disabled={!editable}
            response={response}
          />
        )}
        {item.kind === "ESign" && (
          <ESignItemSurface
            item={item}
            packageId={packageId ?? ""}
            onUpdated={updateItem}
            response={response}
            onSigningBusyChange={setSigningBusy}
            onReload={reload}
          />
        )}
        {item.kind === "Education" && (
          <EducationItemSurface
            item={item}
            onUpdated={updateItem}
            disabled={!editable}
            response={response}
            onSigningBusyChange={setSigningBusy}
          />
        )}
      </>
    );

  const content = (
    <>
      {signingBusy ? (
        <span
          className="text-small text-gray-mid cursor-not-allowed"
          aria-disabled="true"
          title="Please wait — we're still recording your signature."
          data-testid="back-to-checklist-disabled"
        >
          ← Back to checklist
        </span>
      ) : (
        <Link to={`/portal/packages/${packageId}`} className="text-small underline text-onyx">
          ← Back to checklist
        </Link>
      )}

      {error && <p className="text-small text-status-error mt-space-3" role="alert">{error}</p>}

      {!pkg && !error && <p className="text-small text-gray-mid mt-space-3">Loading…</p>}

      {item && (
        <article className="mt-space-4 flex flex-col gap-space-4">
          <header className="flex flex-col gap-space-2">
            {/* flex-wrap: at phone widths the pill drops below a long title
                instead of clipping off the right edge. min-w-0 + break-words
                on the h1: a single long word ("acknowledgement" at 40px)
                otherwise forces the whole page to scroll sideways. */}
            <div className="flex flex-wrap items-start justify-between gap-space-3">
              <h1 className="min-w-0 break-words text-h2 font-display text-onyx m-0">
                {item.templateName}
              </h1>
              <StatusPill
                status={item.status}
                approvalChain={chain}
                data-testid="item-status-pill"
              />
            </div>
            {!chain && (
              <StepIndicator
                kind={item.kind}
                status={item.status}
                requiresSignature={item.requiresSignature}
                signaturePath={item.signaturePath}
                resolvedSignaturePath={item.resolvedSignaturePath}
              />
            )}
            {item.templateDescription && (
              // Instructions/label card: spaced away from the progress stepper
              // above, with a gold top highlight so it reads as guidance rather
              // than just another content card.
              <div className="mt-space-3 border-t-[2px] border-t-gold bg-white-pure rounded-lg shadow-sm p-space-4">
                <p className="text-small text-onyx m-0">{item.templateDescription}</p>
              </div>
            )}
            {item.instructions && (
              <div className="p-space-4 bg-white-pure border border-border-light rounded-lg">
                <PortalMarkdown markdown={item.instructions} />
              </div>
            )}
            {staffNote && (
              <div className="p-space-3 border border-status-warning rounded-md bg-status-warning/10">
                <p className="text-caption font-medium text-status-warning m-0">Needs changes</p>
                <p className="text-small text-onyx m-0 mt-space-1" data-testid="reviewer-note-detail">
                  {staffNote}
                </p>
              </div>
            )}
            {item.hasDocument && !chain && (
              <div className="flex flex-col gap-space-1">
                <Button
                  variant="secondary"
                  onClick={() => void downloadDocument(item.id)}
                  data-testid="download-signed-document"
                >
                  {item.kind === "Upload" ? "Download uploaded file" : "Download signed document"}
                </Button>
                {docError && (
                  <p className="text-small text-status-error m-0" role="alert">
                    {docError}
                  </p>
                )}
              </div>
            )}
          </header>

          {chain ? (
            surfaces(null)
          ) : (
            <ResponseStep key={item.id} item={item} onUpdated={updateItem}>
              {surfaces}
            </ResponseStep>
          )}
        </article>
      )}
    </>
  );

  const pageTitle = item?.templateName ?? "Item";
  return <PortalDetailShell pageTitle={pageTitle}>{content}</PortalDetailShell>;
}
